import React, {
    ForwardedRef, 
    forwardRef, 
    ReactElement, 
    SyntheticEvent, 
    useCallback, 
    useEffect, 
    useState
} from "react";
import {
    Autocomplete,
    Box,
    FormControl, 
    FormLabel,
    TextField,
    Typography,
    useTheme
} from "@mui/material";
import {useController} from "react-hook-form";
import {FieldPath, FieldValues} from "react-hook-form/dist/types";
import {Search} from "@carbon/icons-react";
import {AutocompleteInputProps} from "../../common/types/AutocompleteInputProps";
import {useDebounceValue} from "../../common/hooks/useDebounce";
import {ArrowDropDownIcon} from "@mui/x-date-pickers";

const AutocompleteInput = forwardRef(<T extends FieldValues, TName extends FieldPath<T>, TItem>(
    props: AutocompleteInputProps<T, TName, TItem>,
    ref: ForwardedRef<HTMLSelectElement>
): ReactElement => {

    const {
        items,
        label,
        placeholder,
        error,
        errorMessage,
        hint,
        hideEndAdornment,
        onInputChangeMethod,
        getOptionValue,
        getOptionLabel,
        RenderItemOption,
        fieldMapper,
        valueMapper,
        onSelected,
        startAdornmentContent,
        disableFilteringOption,
        ...rest
    } = props;

    const [innerValue, setInnerValue] = useState<TItem | undefined>();
    const [inputValue, setInputValue] = useState<string>("");
    const [options, setOptions] = useState<TItem[]>([]);

    const {field} = useController({
        ...rest
    });

    const {onChange, value, ref: inputRef, ...otherField} = field;
    const theme = useTheme();

    useEffect(() => {
        if (!value) {
            setInnerValue(value);
        } else if(items != undefined)
        {
            setInnerValue(valueMapper(value, items));
            setInputValue(getOptionLabel(value));
        }

        setOptions(items);
    }, [value, items])

    const combineRefs = useCallback((node: HTMLSelectElement | null) => {
        // Handle the ref from useController (inputRef)
        if (typeof inputRef === 'function') {
            inputRef(node);
        } else if (inputRef) {
            (inputRef as React.MutableRefObject<HTMLSelectElement | null>).current = node;
        }

        // Handle the forwarded ref from the parent component
        if (typeof ref === 'function') {
            ref(node);
        } else if (ref) {
            (ref as React.MutableRefObject<HTMLSelectElement | null>).current = node;
        }
    }, [inputRef, ref]);

    const searchTerm = useDebounceValue(inputValue, 500);
    useEffect(() => {
        onInputChangeMethod(searchTerm)
    },[searchTerm])

    const onInputChange = useCallback(async (event: SyntheticEvent, value: string) => {
        if(innerValue)
        {
            setInnerValue(undefined);
            onChange(undefined);
        }
        setInputValue(value);
    }, [innerValue]);

    const onChanged = useCallback((event: SyntheticEvent<Element, Event>, value: TItem | null) => {
        const finalValue = fieldMapper(value, items);
        onChange(finalValue);
        onSelected?.(value, finalValue);
    }, [fieldMapper, items, onChange]);

    return (
        <Box>
            <FormControl error={error} fullWidth>
                {label && (
                    <FormLabel id={`${field.name}-label`} className={"input-label"} sx={{mb: 0.5}}>
                        {label}
                    </FormLabel>
                )}
                <Autocomplete
                    ref={combineRefs}
                    options={options}
                    {...(disableFilteringOption && { filterOptions: (options) => options })}
                    id={field.name}
                    className={"input-field"}
                    {...otherField}
                    multiple={false}
                    getOptionLabel={getOptionLabel}
                    isOptionEqualToValue={(o, v) => getOptionValue(o) === getOptionValue(v)}
                    slotProps={{
                        paper: {
                            sx: {
                                marginTop: "2px",
                            },
                        },
                    }}
                    popupIcon={hideEndAdornment ? <></> : <ArrowDropDownIcon />}
                    onInputChange={onInputChange}
                    onChange={onChanged}
                    noOptionsText={<Typography>No results found for your search</Typography>}
                    renderOption={(props, option) => {
                        return (
                            <li {...props} key={getOptionLabel(option)}>
                                {RenderItemOption != undefined ? RenderItemOption(option) : getOptionLabel(option)}
                            </li>
                        );
                    }}
                    open={options.length >= 0 && !innerValue && !!inputValue}
                    renderInput={(params) => (
                        <TextField 
                            {...params}
                            InputProps={{
                                sx: {
                                        paddingLeft: 1, 
                                        paddingTop: '0 !important', 
                                        paddingBottom: '0 !important',
                                        bgcolor: theme.palette.thatsMe.surfacePrimary
                                    },
                                ...params.InputProps,
                                startAdornment: startAdornmentContent ? startAdornmentContent : <Search size={26} fill={theme.palette.thatsMe.iconsButton}/>,
                            }}
                            placeholder={placeholder}
                            sx={{mt: 0.5, backgroundColor: theme.palette.thatsMe.surfaceOffWhite}}
                        />
                    )}
                />
                {errorMessage &&
                    <Typography mt={0.5} component="div" className="error-message" variant={"body2"}>
                        {errorMessage}
                    </Typography>
                }
                {hint &&
                    <Typography component="div" variant={"body2"} className={"input-help"}>
                        {hint}
                    </Typography>
                }
            </FormControl>
        </Box>
    );
});

export {AutocompleteInput}
